import { metadata as bpMetadata } from '@rta/rta-blueprint-component';
import { metadata as libraryMetadata } from '@rta/rta-blueprint-library';
import { metadata as tdMetadata } from '@rta/rta-blueprint-3d-component';
import {
	Box,
	Button,
	Flex,
	List,
	ListItem,
	Text,
	Tooltip,
	VStack
} from '@chakra-ui/react';
import { useKeystoneSession } from '../../../auth/use-keystone-session';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { releaseInfo } from '../../../config';
import { Feature, useDeleteAllDraftsFeature, useDeleteCustomerDraftsFeature, usePurgeDimensionsFeature, usePushSubmissionFeature } from '@features';
import { useAppRoles } from '@hooks';

import type { JSX } from "react";

export const AboutPanel = (): JSX.Element => {
	const { status, user } = useKeystoneSession();
	const isAuthenticated = status === 'ready';
	const appRoles = useAppRoles();
	const pushSubmissionFeature = usePushSubmissionFeature();
	const purgeDimensionsFeature = usePurgeDimensionsFeature();
	const buttonOptions: Record<string, Feature<any>> = {
		deleteCustomerDrafts: useDeleteCustomerDraftsFeature(),
		deleteAllDrafts: useDeleteAllDraftsFeature()
	};
	return <ContextPanelContent title={lang.contextPanels.title.about}>
		<VStack spacing={0} align="stretch" h="100%" justifyContent="space-between">
			<Box px="10px" pt=".5em" mt="5px">
				<Text pb="1" fontSize={16} fontWeight={700}>Blueprint Tool</Text>
				<List spacing={0.25} fontSize={14}>
					<ListItem key="build">
						<Flex justifyContent={'space-between'}>
							<Text>Build</Text>
							<Text>{releaseInfo.build}</Text>
						</Flex>
					</ListItem>
					<ListItem key="commit">
						<Flex justifyContent={'space-between'}>
							<Text>Commit</Text>
							<Text>{releaseInfo.commit}</Text>
						</Flex>
					</ListItem>
				</List>
				<Text pb="1" fontSize={16} fontWeight={700} marginTop="15px">User</Text>
				<List spacing={0.25} fontSize={14}>
					<ListItem key="signed-in-as">
						<Flex justifyContent={'space-between'}>
							<Text>Signed in as</Text>
							<Text>{isAuthenticated ? (user?.displayName || user?.email || 'Unknown') : 'Not signed in'}</Text>
						</Flex>
					</ListItem>
					<ListItem key="roles">
						<Flex justifyContent={'space-between'}>
							<Text>Roles</Text>
							<Text>{appRoles.join(', ')}</Text>
						</Flex>
					</ListItem>
				</List>
				<Text pb="1" fontSize={16} fontWeight={700} marginTop="15px">RTA Blueprint Component</Text>
				<List spacing={0.25} fontSize={14}>
					<ListItem key="component">
						<Flex justifyContent={'space-between'}>
							<Text>Version</Text>
							<Text>{bpMetadata?.blueprintComponentVersion ?? 'N/A'}</Text>
						</Flex>
					</ListItem>
				</List>

				<Text pb="1" fontSize={16} fontWeight={700} marginTop="15px">RTA 3D Blueprint Component</Text>
				<List spacing={0.25} fontSize={14}>
					<ListItem key="component">
						<Flex justifyContent={'space-between'}>
							<Text>Version</Text>
							<Text>{tdMetadata?.blueprint3dComponentVersion ?? 'N/A'}</Text>
						</Flex>
					</ListItem>
				</List>

				<Text pb="1" fontSize={16} fontWeight={700} marginTop="15px">RTA Blueprint Library</Text>
				<List spacing={0.25} fontSize={14}>
					<ListItem key="library">
						<Flex justifyContent={'space-between'}>
							<Text>Version</Text>
							<Text>{libraryMetadata?.blueprintLibraryVersion ?? 'N/A'}</Text>
						</Flex>
					</ListItem>
				</List>
				<Box display="flex" mt="50px" justifyContent="center">
					<VStack width="100%">
						<Tooltip label={pushSubmissionFeature.tooltip} aria-label={pushSubmissionFeature.tooltip} placement="top">
							<Button size="sm" w="100%" onClick={pushSubmissionFeature.handler} isDisabled={pushSubmissionFeature.disabled} hidden={pushSubmissionFeature.hidden}>{pushSubmissionFeature.label}</Button>
						</Tooltip>
						<Tooltip label={purgeDimensionsFeature.tooltip} aria-label={purgeDimensionsFeature.tooltip} placement="top">
							<Button size="sm" w="100%" onClick={purgeDimensionsFeature.handler} isDisabled={purgeDimensionsFeature.disabled} hidden={purgeDimensionsFeature.hidden}>{purgeDimensionsFeature.label}</Button>
						</Tooltip>
					</VStack>
				</Box>
			</Box>
			<Box px="10px" pb=".5em" mb="5px" display="flex" gap="10px" alignItems="center" flexDirection="column">
				{ Object.keys(buttonOptions).map((key) => {
					const buttonItem = buttonOptions[key];
					return (
						<Tooltip key={key} label={buttonItem.tooltip} aria-label={buttonItem.tooltip}>
							<Button size="sm" w="100%" key={key} isDisabled={buttonItem.disabled} onClick={buttonItem.handler} hidden={buttonItem.hidden}>{buttonItem.label}</Button>
						</Tooltip>
					);
				})}
			</Box>
		</VStack>
	</ContextPanelContent>;
};
